import axios from 'axios'
import { API_BASE_URL } from '@/utils/config'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // Αύξηση σε 30 δευτερόλεπτα
  withCredentials: true, // Enable sending cookies/sessions with requests
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add authorization token if exists (check both 'token' and 'authToken')
    const token = localStorage.getItem('token') || localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    return Promise.reject(error)
  }
)

export interface User {
  id?: number
  name: string
  email: string
  password?: string
  role?: string
  created_at?: string
  updated_at?: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface LoginResponse {
  success: boolean
  message: string
  user: {
    id: number
    name: string
    email: string
    role?: string
  }
  token?: string
}

export interface Category {
  id?: number
  name: string
  slug?: string
  description?: string
  parent_id?: number | null
  status: 'active' | 'inactive'
  sort_order?: number
  products_count?: number
  created_at?: string
}

export interface Brand {
  id?: number
  name: string
  slug?: string
  logo?: string
  description?: string
  website?: string
  status: 'active' | 'inactive'
  products_count?: number
  created_at?: string
}

export const userService = {
  // Get all users
  async getUsers() {
    const response = await api.get('/admin-users.php')
    return response.data
  },

  // Get single user
  async getUser(id: number) {
    const response = await api.get(`/admin-users.php?id=${id}`)
    return response.data
  },

  // Create user
  async createUser(user: User) {
    const response = await api.post('/admin-users.php', user)
    return response.data
  },

  // Update user
  async updateUser(user: User) {
    const response = await api.put('/admin-users.php', user)
    return response.data
  },

  // Delete user
  async deleteUser(id: number) {
    const response = await api.delete('/admin-users.php', { data: { id } })
    return response.data
  },

  // Login
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post('/login.php', credentials)
    return response.data
  },
}

export const categoryService = {
  // Get all categories
  async getCategories() {
    const response = await api.get(`/categories.php?_t=${Date.now()}`)
    return response.data
  },

  // Create category
  async createCategory(category: Category) {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken')
    const response = await api.post('/categories.php', category, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
    return response.data
  },

  // Update category
  async updateCategory(category: Category) {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken')
    const response = await api.put('/categories.php', category, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
    return response.data
  },

  // Delete category
  async deleteCategory(id: number) {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken')
    const response = await api.delete(`/categories.php?id=${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
    return response.data
  },
}

export const brandService = {
  // Get all brands
  async getBrands() {
    const response = await api.get('/brands.php')
    return response.data
  },

  // Create brand
  async createBrand(brand: Brand) {
    const response = await api.post('/brands.php', brand)
    return response.data
  },

  // Update brand
  async updateBrand(brand: Brand) {
    const response = await api.put('/brands.php', brand)
    return response.data
  },

  // Delete brand
  async deleteBrand(id: number) {
    const response = await api.delete('/brands.php', { data: { id } })
    return response.data
  },
}

export const settingsService = {
  // Get content settings (legacy)
  async getContent() {
    const response = await api.get('/content.php')
    return response.data
  },

  // Get settings from new getSettings endpoint
  async getSettings() {
    const response = await api.get('/settings.php?action=getSettings')
    return response.data
  },

  // Save content settings
  async saveContent(content: any) {
    const response = await api.post('/content.php', content)
    return response.data
  },

  // Get gallery images
  async getGalleryImages() {
    const response = await api.get('/settings.php?action=gallery')
    return response.data
  },

  // Upload image
  async uploadImage(formData: FormData) {
    const response = await api.post('/settings.php?action=upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Update gallery image metadata
  async updateGalleryImage(data: any) {
    const response = await api.put('/settings.php?action=gallery', data)
    return response.data
  },

  // Delete image
  async deleteImage(id: number) {
    const response = await api.delete('/settings.php?action=gallery', { data: { id } })
    return response.data
  },

  // Copy gallery image
  async copyGalleryImage(data: any) {
    const response = await api.post('/settings.php?action=copy_gallery_image', data)
    return response.data
  },

  // Get general settings
  async getGeneralSettings() {
    const response = await api.get('/settings.php')
    return response.data
  },

  // Save general settings
  async saveGeneralSettings(settings: any) {
    const response = await api.post('/settings.php', settings)
    return response.data
  },

  // Update individual settings (new method for footer settings)
  async updateSettings(settings: any) {
    const response = await api.post('/settings.php', {
      action: 'updateSettings',
      ...settings
    })
    return response.data
  },

  // Get system info
  async getSystemInfo() {
    const response = await api.get('/system-info.php')
    return response.data
  },

  // Get navigation settings including page header styles
  async getNavigationSettings() {
    const response = await api.get('/settings.php?action=get-navigation-settings')
    return response.data
  },
}

export default api
